import DairyService from '@src/api/services/dairy';
import { RouteTypes } from '@src/routes/constants';
import React, { useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import Layout from '@src/components/Layout';
import { El } from './style';
import { Dimmer, Loader, Tab } from 'semantic-ui-react';
import { Outlet, useLocation } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { ThemeContext } from 'styled-components';
import CustomIcon from '~/components/CustomIcon';
import { useAuth } from '~/routes/components/AuthProvider';
import { handleErrors, onError } from '~/utils/helpers';
import { ADMIN_ROLE_SLUG, BASE_PRICE_TEXT_SLUG, DAIRY_USERS_PROFILE_COUNT_USER_ID, LIGHT_USER_ROLE_SLUG, MILK_MONEY_ACCOUNT_SLUG, OPERATIVE_ROLE_SLUG } from '~/Constants';

const rolesWithPermissionViewEdit = [ADMIN_ROLE_SLUG, OPERATIVE_ROLE_SLUG, LIGHT_USER_ROLE_SLUG]
const rolesAdminLight = [ADMIN_ROLE_SLUG, LIGHT_USER_ROLE_SLUG]
const rolesWithEditPermissions = [ADMIN_ROLE_SLUG, OPERATIVE_ROLE_SLUG]

const DairyFactoryProfile: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { systemData, systemData: { modules, operativeUsersCanSeeFarmersInfo, logged: { roleSlug } } } = useAuth()
  const { logoTitle, firstColor } = useContext(ThemeContext)
  const [counters, setCounters] = useState({
    farmers: 0,
    users: 0,
    farmerGroups: 0
  })
  const [isBusy, setIsBusy] = useState(false)
  const [basePrice, setBasePrice] = useState(systemData?.texts.find(text => text.slug === BASE_PRICE_TEXT_SLUG))

  const operativeUserLogged = roleSlug === OPERATIVE_ROLE_SLUG
  const milkMoneyModuleEnabled = modules.find(module => module.slug === MILK_MONEY_ACCOUNT_SLUG)

  useEffect(() => {
    if (roleSlug !== OPERATIVE_ROLE_SLUG) {
      fetchCounters()
    } else if (operativeUserLogged && operativeUsersCanSeeFarmersInfo === 1) {
      fetchCounters()
    }
  }, [])

  useEffect(() => {
    setBasePrice(systemData?.texts.find(text => text.slug === BASE_PRICE_TEXT_SLUG))
  }, [t, systemData])

  async function fetchCounters() {
    setIsBusy(true)
    try {
      const farmersResponse = await DairyService.countAllActiveFarmers()
      const usersResponse = await DairyService.countAllActiveUsers()
      const farmerGroupsResponse = await DairyService.countAllActiveFarmerGroups()
      setCounters({
        farmers: farmersResponse.data,
        users: usersResponse.data,
        farmerGroups: farmerGroupsResponse.data
      })
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  const panes = [
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'users-tab',
        content: t('Dairy users'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_USERS,
        key: 'users',
        display: operativeUsersCanSeeFarmersInfo === 1 ? rolesWithPermissionViewEdit : rolesAdminLight
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'farmer-groups-tab',
        content: t('Farmer Groups'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_FARMER_GROUPS,
        key: 'farmer-groups',
        display: operativeUsersCanSeeFarmersInfo === 1 ? rolesWithPermissionViewEdit : rolesAdminLight
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'locations-tab',
        content: t('Locations'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_LOCATIONS,
        key: 'locations',
        display: rolesWithPermissionViewEdit
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'individual-filters-tab',
        content: t('Individual filters'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_INDIVIDUAL_FILTERS,
        key: 'individual-filters',
        display: rolesWithEditPermissions
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'milk-types-tab',
        content: t('Milk types'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_MILK_TYPES,
        key: 'milk-types',
        display: rolesWithPermissionViewEdit
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'milk-payment-tab',
        content: t('Export MGA'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_MILK_PAYMENT,
        key: 'milk-payment',
        display: rolesWithEditPermissions
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'banks-tab',
        content: t('Banks'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_BANKS,
        key: 'banks',
        display: operativeUsersCanSeeFarmersInfo === 1 ? rolesWithPermissionViewEdit : rolesAdminLight
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'vat-tab',
        content: t('VAT'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_VAT,
        key: 'vat',
        display: rolesWithEditPermissions
      }
    }
    ,
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'base-price-tab',
        content: basePrice?.translation || basePrice?.label,
        to: RouteTypes.DAIRY_FACTORY_PROFILE_BASE_PRICE,
        key: 'base-price',
        display: rolesWithPermissionViewEdit
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'husbandry-tab',
        content: t('Forms of husbandry'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_HUSBANDRY,
        key: 'husbandry',
        display: rolesWithPermissionViewEdit
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'shop-managment-tab',
        content: t('Shop managment'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_SHOP,
        key: 'shop-managment',
        display: milkMoneyModuleEnabled ? rolesWithPermissionViewEdit : []
      }
    }
  ];

  const { data: dairyProfileData, isLoading: isDairyProfileDataLoading } = useQuery(
    'profile',
    DairyService.getDairyProfile,
    {
      onError,
      retry: 1,
      refetchOnMount: true,
      refetchOnWindowFocus: false
    }
  );

  function renderCounter() {
    return < El.DairyDetailsCounter firstColor={firstColor}>
      <div style={{ position: 'relative', display: 'flex' }}>
        <div className='dairy-details-counter-column'>
          <CustomIcon className='icon-farmer-icon font-size-4' size='big' />
          <div className='label'>{t('Farmers')}</div>
          <div className='value'>{counters.farmers}</div>
        </div>
        <div className='dairy-details-counter-column'>
          <CustomIcon className='icon-user-group font-size-4' size='big' />
          <div className='label'>{t('Users')}</div>
          <div className='value' id={DAIRY_USERS_PROFILE_COUNT_USER_ID}>{counters.users}</div>
        </div>
        <div className='dairy-details-counter-column'>
          <CustomIcon className='icon-teamwork font-size-4' size='big' />
          <div className='label'>{t('Farmer groups')}</div>
          <div className='value'>{counters.farmerGroups}</div>
        </div>
        <Dimmer inverted active={isBusy}>
          <Loader active={isBusy} />
        </Dimmer>
      </div>
    </El.DairyDetailsCounter>
  }

  const renderDairyDetailsCardBody = () => (
    <El.DairyProfileCardBody>
      <El.DairyProfileLogo>
        <div className='dairy-logo-container'>
          <LazyLoadImage
            alt={''}
            effect='blur'
            src={logoTitle} />
        </div>
        <div className='dairy-name-container'>
          <div className='dairy-name'>{dairyProfileData?.companyName}</div>
        </div>
      </El.DairyProfileLogo>
      {roleSlug !== OPERATIVE_ROLE_SLUG || operativeUsersCanSeeFarmersInfo === 1 ? renderCounter() : null}
    </El.DairyProfileCardBody >
  );

  function renderDairyDetailsCardFooter() {
    const panesFiltered = panes?.filter(pane => pane.menuItem.display.find(item => item === roleSlug))
    const index = panesFiltered.findIndex(pane => pane.menuItem.to === location.pathname)
    return <El.DiaryProfileCardFooter>
      <Tab
        activeIndex={index}
        menu={{ secondary: true, pointing: true }} panes={panesFiltered}
      />
    </El.DiaryProfileCardFooter>
  }

  return (
    <Layout
      segmented
      isBusy={isDairyProfileDataLoading}
      title={t('Dairy Factory')}
    >
      <El.DairyProfileCard>
        <div>
          {renderDairyDetailsCardBody()}
        </div>
        {renderDairyDetailsCardFooter()}
      </El.DairyProfileCard>
      <Outlet />
    </Layout>
  );
};

export default DairyFactoryProfile;
